/**
 * ProtocolsCatalogTab — Sprint 6.5
 *
 * Catalogue des protocoles (MAIN + COMPLEMENTARY) pour SUPER_ADMIN_SAAS et
 * ADMIN_CLINIQUE. Liste + filtres + activation/désactivation + création
 * clinique-scoped + suppression (clinique uniquement).
 *
 * Règles RBAC :
 * - SUPER_ADMIN_SAAS : voit + modifie tout (SEED globaux + clinique-scoped)
 * - ADMIN_CLINIQUE   : voit SEED (read-only) + son catalogue clinique (écriture)
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Search, Check, X, Trash2, Edit3, FlaskConical, Pill, Globe, Building2
} from "lucide-react";
import api from "@/api";
import { useTreatmentReferentials } from "@/components/shared/OncologyContext";

const CANCER_TYPES = [
  "", "Sein", "Colon / Rectum", "Poumon", "Poumon petites cellules",
  "Pancreas", "Ovaire", "Vessie", "Testicule", "Prostate",
  "Foie (CHC)", "Estomac / Gastrique", "ORL", "Col uterin",
  "Lymphome non hodgkinien", "Lymphome de Hodgkin", "Myelome multiple",
  "Sarcomes", "Autre"
];

const COMP_CATEGORIES = [
  "", "ANTIEMETIC", "CORTICOID", "GCSF", "EPO", "BISPHOSPHONATE",
  "DECAPEPTYL", "HYDRATATION", "PREMED_SPECIFIC", "OTHER"
];

export default function ProtocolsCatalogTab({ currentUserRole }) {
  const [tab, setTab] = useState("MAIN");
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cancerFilter, setCancerFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("ALL"); // ALL | SEED | CLINIC
  const [showCreate, setShowCreate] = useState(false);

  const canEditSeed = currentUserRole === "SUPER_ADMIN_SAAS";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind: tab, limit: "500" });
      if (cancerFilter) params.append("cancerType", cancerFilter);
      if (categoryFilter) params.append("category", categoryFilter);
      if (search) params.append("search", search);
      const { data } = await api.get(`/reference-protocols?${params}`);
      setProtocols(Array.isArray(data) ? data : data?.items || []);
    } catch {
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, cancerFilter, categoryFilter]);

  const filtered = useMemo(() => {
    return protocols.filter(p => {
      if (sourceFilter === "SEED" && p.clinicId) return false;
      if (sourceFilter === "CLINIC" && !p.clinicId) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!p.label?.toLowerCase().includes(s) && !p.code?.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [protocols, sourceFilter, search]);

  const toggleActive = async (p) => {
    try {
      await api.patch(`/reference-protocols/${p.code}`, { active: !p.active });
      toast.success(p.active ? "Désactivé" : "Activé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const removeProtocol = async (p) => {
    if (!window.confirm(`Supprimer le protocole "${p.label}" ?\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/reference-protocols/${p.code}`);
      toast.success("Protocole supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-4" data-testid="protocols-catalog-tab">
      {/* Tabs kind */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { k: "MAIN", label: "Protocoles principaux", icon: FlaskConical },
          { k: "COMPLEMENTARY", label: "Complémentaires", icon: Pill },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-violet-600 text-violet-700" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`tab-${t.k.toLowerCase()}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Filters bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher code ou label…"
            className="pl-9 pr-3 py-2 w-full border border-slate-200 rounded-lg text-sm"
            data-testid="protocols-search"
          />
        </div>
        {tab === "MAIN" && (
          <select
            value={cancerFilter}
            onChange={e => setCancerFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="cancer-filter"
          >
            {CANCER_TYPES.map(c => (
              <option key={c || "all"} value={c}>{c || "— Tous cancers —"}</option>
            ))}
          </select>
        )}
        {tab === "COMPLEMENTARY" && (
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            data-testid="category-filter"
          >
            {COMP_CATEGORIES.map(c => (
              <option key={c || "all"} value={c}>{c || "— Toutes catégories —"}</option>
            ))}
          </select>
        )}
        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          data-testid="source-filter"
        >
          <option value="ALL">— Source: Tous —</option>
          <option value="SEED">Catalogue global</option>
          <option value="CLINIC">Catalogue clinique</option>
        </select>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5"
          data-testid="create-protocol-btn"
        >
          <Plus className="w-4 h-4" />
          Nouveau
        </button>
      </div>

      {/* Counter */}
      <p className="text-xs text-slate-500">
        {filtered.length} protocole{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}
      </p>

      {/* Table */}
      <div className="dash-card overflow-x-auto p-0">
        <table className="w-full min-w-[900px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Code</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Label</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">{tab === "MAIN" ? "Cancer" : "Catégorie"}</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">DCI</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Source</th>
              <th className="text-center p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Statut</th>
              <th className="text-right p-4 text-sm font-medium text-slate-600 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Chargement…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Aucun protocole</td></tr>
            ) : filtered.map(p => {
              const isSeed = !p.clinicId;
              const canEdit = !isSeed || canEditSeed;
              return (
                <tr key={p.code} className="hover:bg-slate-50">
                  <td className="p-4 font-mono text-xs text-slate-700">{p.code}</td>
                  <td className="p-4 text-sm text-slate-900">{p.label}</td>
                  <td className="p-4 text-sm text-slate-600">
                    {tab === "MAIN" ? p.cancerType : p.category}
                  </td>
                  <td className="p-4 text-xs text-slate-500">
                    {(p.components || []).map(c => c.dci).join(", ") || "—"}
                  </td>
                  <td className="p-4 text-xs">
                    {isSeed ? (
                      <span className="inline-flex items-center gap-1 text-slate-600"><Globe className="w-3 h-3" />Global</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-violet-700"><Building2 className="w-3 h-3" />Clinique</span>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => canEdit && toggleActive(p)}
                      disabled={!canEdit}
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                        p.active
                          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {p.active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {p.active ? "Actif" : "Inactif"}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    {!isSeed && canEdit && (
                      <button
                        onClick={() => removeProtocol(p)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Supprimer"
                        data-testid={`delete-${p.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateProtocolModal
          kind={tab}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateProtocolModal({ kind, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [cancerType, setCancerType] = useState("");
  const [category, setCategory] = useState(kind === "COMPLEMENTARY" ? "OTHER" : "");
  const [therapeuticLines, setTherapeuticLines] = useState([]);
  const { therapeuticLines: lineOptions } = useTreatmentReferentials();
  const toggleLine = (l) => setTherapeuticLines(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
  const [components, setComponents] = useState([{ dci: "", doseFormula: "", route: "IV", days: "J1" }]);
  const [submitting, setSubmitting] = useState(false);

  const addComponent = () => setComponents(prev => [...prev, { dci: "", doseFormula: "", route: "IV", days: "J1" }]);
  const removeComponent = (idx) => setComponents(prev => prev.filter((_, i) => i !== idx));
  const updateComponent = (idx, field, value) => setComponents(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));

  const submit = async () => {
    if (!label.trim()) return toast.error("Label requis");
    const validComps = components.filter(c => c.dci.trim());
    if (validComps.length === 0) return toast.error("Au moins 1 composant DCI requis");

    setSubmitting(true);
    try {
      const payload = {
        label: label.trim(),
        kind,
        cancerType: kind === "MAIN" ? (cancerType || null) : null,
        category: kind === "COMPLEMENTARY" ? category : null,
        therapeuticLines,
        components: validComps,
        biosimilarsMA: {},
      };
      await api.post("/reference-protocols", payload);
      toast.success("Protocole créé");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="create-protocol-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-slate-900">Nouveau protocole {kind === "MAIN" ? "principal" : "complémentaire"}</h3>
            <p className="text-xs text-slate-500 mt-0.5">Enregistré dans le catalogue de votre clinique</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Label <span className="text-red-500">*</span></label>
            <input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Ex : Herceptin + Perjeta + Taxol"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="new-label-input"
            />
          </div>
          {kind === "MAIN" ? (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Cancer</label>
                <select value={cancerType} onChange={e => setCancerType(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  {CANCER_TYPES.map(c => <option key={c || "all"} value={c}>{c || "— Non défini —"}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Lignes thérapeutiques (référentiel)</label>
                <div className="flex flex-wrap gap-1.5" data-testid="protocol-therapeutic-lines">
                  {lineOptions.map(l => (
                    <button key={l} type="button" onClick={() => toggleLine(l)} data-testid={`protocol-line-${l}`}
                      className={`px-2.5 py-1 rounded-full text-xs border ${therapeuticLines.includes(l) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                {COMP_CATEGORIES.filter(Boolean).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Composants (DCI)</label>
              <button onClick={addComponent} className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
                <Plus className="w-3 h-3" /> Ajouter
              </button>
            </div>
            <div className="space-y-2">
              {components.map((c, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    value={c.dci}
                    onChange={e => updateComponent(idx, "dci", e.target.value)}
                    placeholder="DCI (ex: Trastuzumab)"
                    className="col-span-4 px-2 py-1.5 border border-slate-200 rounded text-xs"
                  />
                  <input
                    value={c.doseFormula}
                    onChange={e => updateComponent(idx, "doseFormula", e.target.value)}
                    placeholder="Dose (ex: 8 mg/kg)"
                    className="col-span-3 px-2 py-1.5 border border-slate-200 rounded text-xs"
                  />
                  <select value={c.route} onChange={e => updateComponent(idx, "route", e.target.value)} className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-xs bg-white">
                    <option>IV</option><option>PO</option><option>SC</option><option>IM</option><option>SL</option>
                  </select>
                  <input
                    value={c.days}
                    onChange={e => updateComponent(idx, "days", e.target.value)}
                    placeholder="Jours"
                    className="col-span-2 px-2 py-1.5 border border-slate-200 rounded text-xs"
                  />
                  <button onClick={() => removeComponent(idx)} className="col-span-1 p-1 text-red-500 hover:bg-red-50 rounded" disabled={components.length === 1}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-2 justify-end">
          <button onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Annuler</button>
          <button onClick={submit} disabled={submitting} className="px-4 py-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 rounded-lg" data-testid="new-protocol-submit">
            {submitting ? "Création…" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}
